import React, {useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useLocation, useNavigate} from "react-router-dom";
import {Button} from "primereact/button";
import {Message} from "primereact/message";
import {Badge} from "primereact/badge";
import {OverlayPanel} from "primereact/overlaypanel";
import Avatar from "../../system/components/avatar";
import {logout} from "../../auth/auth-slice";
import {useTranslate} from "../../system/useTranslate";
import {usePermission} from "../../system/usePermission";
import {useWorkspaceRole} from "../../system/use-workspace-role";
import {
    getEffectivePermissions,
    resolveActiveWorkspaceUserRoles,
    shouldBlockWorkspaceMutation,
} from "../../system/workspace-permissions";
import {useTask} from "../../models/task/use-task";
import {useDeliverable} from "../../models/deliverable/use-deliverable";
import {useProject} from "../../models/project/use-project";
import {filterMyTasksScope} from "../../models/task/my-tasks-scope";
import {filterMyProjectsScope} from "../../models/project/my-projects-scope";
import {setMessage} from "../../system/app-slice";
import {upsertWorkspaceProject, upsertWorkspaceTask, workspaceGraphSelectors} from "../../system/workspace-graph-slice";
import SelectWorkspace from "../workspace/components/select-workspace";
import {CreateWorkspace} from "../workspace/init";
import MobileQuickCreate from "./mobile-quick-create";
import MobileTasks from "./mobile-tasks";
import MobileMessages from "./mobile-messages";
import NotificationSettings from "../notification-settings";
import MobileSettings, {MobileProfile} from "./mobile-settings";
import {
    MobileHome,
    MobileMore,
    MobileResource,
    moreItems,
} from "./mobile-resources";
import "./mobile-theme.css";
import {belongsToWorkspace} from "../inbox/inbox-utils";

const navigationItems = [
    {key: "home", path: "/mobile", icon: "pi pi-home"},
    {key: "tasks", path: "/mobile/tasks", icon: "pi pi-check-circle"},
    {key: "messages", path: "/mobile/messages", icon: "pi pi-bell"},
    {key: "more", path: "/mobile/more", icon: "pi pi-ellipsis-h"},
];

const legacyRoutes = {
    "/": "/mobile",
    "/home": "/mobile",
    "/tasks": "/mobile/tasks",
    "/inbox": "/mobile/messages",
    "/projects": "/mobile/projects",
    "/goals": "/mobile/goals",
    "/teams": "/mobile/teams",
    "/workspace": "/mobile/workspace",
    "/settings": "/mobile/settings",
    "/help": "/mobile/help",
    "/update-log": "/mobile/updates",
    "/notification-settings": "/mobile/notification-settings",
};

const entityKey = (entity) => entity?.documentId || entity?.id;
const uniqueEntities = (entities = []) => Array.from(entities.reduce((items, entity) => {
    const key = entityKey(entity);
    if (key !== null && key !== undefined) {
        items.set(String(key), {...(items.get(String(key)) || {}), ...entity});
    }
    return items;
}, new Map()).values());

// notification links (?task= / ?goal= / ?project= / ?chat=) open the matching mobile detail page
function notificationDetailPath(pathname, search) {
    const params = new URLSearchParams(search || "");
    const encode = (value) => encodeURIComponent(value);
    if (params.get("task")) return `/mobile/tasks/${encode(params.get("task"))}`;
    if (params.get("goal")) return `/mobile/goals/${encode(params.get("goal"))}`;
    if (params.get("project")) return `/mobile/projects/${encode(params.get("project"))}`;
    if (params.get("deliverable")) return "/mobile/projects";
    if (pathname === "/inbox" && params.get("chat")) return `/mobile/messages/${encode(params.get("chat"))}`;
    return null;
}

function normalizeMobilePath(pathname, search = "") {
    const detail = notificationDetailPath(pathname, search);
    if (detail) return detail;
    if (legacyRoutes[pathname]) return legacyRoutes[pathname];
    const detailPrefixes = ["tasks", "projects", "goals", "teams"];
    const legacyDetail = detailPrefixes.find((prefix) => pathname.startsWith(`/${prefix}/`));
    return legacyDetail ? `/mobile${pathname}` : pathname.startsWith("/mobile") ? pathname : "/mobile";
}

function Mobile() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const translate = useTranslate();
    const permission = usePermission();
    const taskApi = useTask();
    const deliverableApi = useDeliverable();
    const projectApi = useProject();
    const {workspaceRole} = useWorkspaceRole();
    const {isDesktop, workspaceMembershipData, notifications = []} = useSelector((state) => state.app);
    const {user} = useSelector((state) => state.auth);
    const graphMembership = useSelector((state) => state.workspaceGraph.workspaceMembership);
    const graphUserRoles = useSelector((state) => state.workspaceGraph.userRoles);
    const graphProjects = useSelector(workspaceGraphSelectors.projects);
    const graphTasks = useSelector(workspaceGraphSelectors.tasks);
    const [quickCreateVisible, setQuickCreateVisible] = useState(false);
    const [quickCreateLoading, setQuickCreateLoading] = useState(false);
    const [quickCreateError, setQuickCreateError] = useState("");
    const workspaceMenuRef = useRef(null);
    const profileMenuRef = useRef(null);
    const workspaceStatus = workspaceMembershipData?.workspace?.workspaceStatus || "active";
    const workspaceRestricted = workspaceStatus !== "active";
    const readOnly = workspaceRestricted || shouldBlockWorkspaceMutation(workspaceRole);
    const activeUserRoles = resolveActiveWorkspaceUserRoles(workspaceMembershipData, graphMembership, graphUserRoles);
    const canCreateTaskWithoutProject = permission.can("create-task") || workspaceRole?.role === "admin";
    const canCreateInProject = (permissionKey, project) => {
        if (permission.can(permissionKey) || workspaceRole?.role === "admin") return true;
        const scopeId = project?.documentId || project?.id;
        if (!scopeId) return false;
        const scopedPermissions = getEffectivePermissions(activeUserRoles, scopeId, project?.rolesConfig || {});
        return scopedPermissions.includes("*") || scopedPermissions.includes(permissionKey);
    };
    const workspace = workspaceMembershipData?.workspace;
    const projects = uniqueEntities([...(graphProjects || []), ...(workspace?.projects || [])]);
    const visibleProjects = filterMyProjectsScope(projects, workspace, workspaceMembershipData);
    const allowedProjects = visibleProjects.filter((project) => canCreateInProject("create-task", project));
    const allowedMilestoneProjects = visibleProjects.filter((project) => canCreateInProject("create-milestone", project));
    const personalTaskSource = uniqueEntities([
        ...(graphTasks || []),
        ...(workspace?.tasks || []),
        ...projects.flatMap((project) => project?.tasks || []),
    ]);
    const personalTasks = filterMyTasksScope(personalTaskSource, workspace, workspaceMembershipData);
    const visibleProjectTasks = visibleProjects.flatMap((project) => (project?.tasks || [])
        .filter((task) => !task?.archivedAt)
        .map((task) => ({
            ...task,
            workspace: task.workspace || workspace,
            projects: task.projects?.length ? task.projects : [project],
        })));
    const visibleTasks = uniqueEntities([...personalTasks, ...visibleProjectTasks]);
    const allowedTasks = visibleTasks.filter((task) => {
        const taskProjects = task?.projects || [];
        if (!taskProjects.length) return canCreateTaskWithoutProject;
        return taskProjects.some((taskProject) => {
            const project = projects.find((item) => String(entityKey(item)) === String(entityKey(taskProject))) || taskProject;
            return canCreateInProject("create-task", project);
        });
    });
    const quickTaskProjects = allowedProjects.length ? allowedProjects : visibleProjects;
    const quickTasks = allowedTasks.length ? allowedTasks : visibleTasks;
    const quickMilestoneProjects = allowedMilestoneProjects.length ? allowedMilestoneProjects : visibleProjects;
    const taskDestinations = [
        ...quickTaskProjects.map((project) => ({
            key: `project-${entityKey(project)}`,
            type: "project",
            item: project,
            label: translate("mobile.quick-create.project-option").replace("{{title}}", project.title),
        })),
        ...quickTasks.map((task) => ({
            key: `task-${entityKey(task)}`,
            type: "task",
            item: task,
            label: translate("mobile.quick-create.task-option").replace("{{title}}", task.title),
        })),
    ];
    const milestoneDestinations = quickMilestoneProjects.map((project) => ({
        key: `milestone-project-${entityKey(project)}`,
        type: "project",
        item: project,
        label: translate("mobile.quick-create.project-option").replace("{{title}}", project.title),
    }));
    const canCreateProject = permission.can("create-new-projects") || workspaceRole?.role === "admin";
    const projectDestinations = !readOnly && canCreateProject && workspace ? [{
        key: `workspace-${entityKey(workspace)}`,
        type: "workspace",
        item: workspace,
        label: translate("mobile.quick-create.workspace-option").replace("{{title}}", workspace.name),
    }] : [];
    const quickCreateTypes = [
        {value: "task", icon: "pi pi-list-check", label: translate("mobile.quick-create.task"), destinations: taskDestinations, disabled: false},
        {value: "milestone", icon: "pi pi-flag", label: translate("mobile.quick-create.milestone"), destinations: milestoneDestinations, disabled: false},
        {value: "project", icon: "pi pi-folder", label: translate("mobile.quick-create.project"), destinations: projectDestinations, disabled: !projectDestinations.length},
    ];
    const mobilePath = normalizeMobilePath(location.pathname, location.search);
    const parts = mobilePath.replace(/^\/mobile\/?/, "").split("/").filter(Boolean);
    const section = parts[0] || "home";
    const detailId = parts[1] || null;
    const isGuest = workspaceRole?.role === "guest";
    const effectiveSection = isGuest && section !== "goals"
        ? "goals" : section;

    const visibleMoreItems = useMemo(() => moreItems.filter((item) => {
        if (item.permission && !permission.can(item.permission)) return false;
        if (workspaceRole?.role === "guest") return item.key === "goals";
        return true;
    }), [permission, workspaceRole]);

    useEffect(() => {
        if (isDesktop) return undefined;
        document.body.classList.add("shabnam-font", "mobile-shell-active");
        document.documentElement.classList.add("mobile-shell-active");
        return () => {
            document.body.classList.remove("shabnam-font", "mobile-shell-active");
            document.documentElement.classList.remove("mobile-shell-active");
        };
    }, [isDesktop]);

    if (isDesktop) return null;

    const member = workspaceMembershipData?.member || user;
    const workspaceName = workspaceMembershipData?.workspace?.name || translate("mobile-shell.workspace-fallback");
    const displayName = member?.fullname || member?.firstname || translate("mobile-shell.user-fallback");
    const unreadCount = notifications.filter((item) => (
        !item.isRead && item.displayType !== "invisible" && belongsToWorkspace(item, workspaceMembershipData?.workspace)
    )).length;

    const handleWorkspaceClick = (event) => workspaceMenuRef.current?.toggle(event);
    const handleProfileClick = (event) => profileMenuRef.current?.toggle(event);
    const openQuickCreate = () => {
        setQuickCreateError("");
        setQuickCreateVisible(true);
    };
    const createQuickItem = async ({type, title, destination}) => {
        setQuickCreateLoading(true);
        setQuickCreateError("");
        try {
            let target = null;
            if (type !== "project") {
                const allowedDestinations = quickCreateTypes.find((item) => item.value === type)?.destinations || [];
                if (!destination || !allowedDestinations.some((item) => item.key === destination.key)) throw new Error("quick-create-forbidden");
                target = destination.item;
            }

            if (type === "task") {
                const targetProjects = destination.type === "project"
                    ? [target.id]
                    : (target.projects || []).map((project) => project.id).filter(Boolean);
                const created = await taskApi.createTask({
                    title,
                    taskStatus: "todo",
                    workspace: workspace?.id,
                    projects: targetProjects,
                    ...(destination.type === "task" ? {parentTask: target.id} : {}),
                });
                if (!created) throw new Error("task-create-failed");
                dispatch(upsertWorkspaceTask(created));
                if (destination.type === "task") {
                    dispatch(upsertWorkspaceTask({
                        ...target,
                        childrenTasks: uniqueEntities([...(target.childrenTasks || []), created]),
                    }));
                }
            } else if (type === "milestone") {
                const created = await deliverableApi.createDeliverable({
                    title,
                    deliverableStatus: "todo",
                    workspace: workspace?.id,
                    project: target.id,
                });
                if (!created) throw new Error("milestone-create-failed");
                dispatch(upsertWorkspaceProject({
                    ...target,
                    deliverables: uniqueEntities([...(target.deliverables || []), created]),
                }));
            } else if (type === "project") {
                if (readOnly || !canCreateProject || !workspace) throw new Error("quick-create-forbidden");
                const created = await projectApi.createProject({
                    title,
                    projectStatus: "on-way",
                    workspace: workspace.id,
                    owner: workspaceMembershipData?.id,
                    creatorUser: workspaceMembershipData?.id,
                });
                if (!created) throw new Error("project-create-failed");
                dispatch(upsertWorkspaceProject(created));
            } else {
                throw new Error("quick-create-type-invalid");
            }
            setQuickCreateVisible(false);
            dispatch(setMessage({severity: "success", content: translate(`mobile.quick-create.${type}-success`)}));
        } catch (requestError) {
            const message = translate("mobile.common.mutation-error");
            setQuickCreateError(message);
        } finally {
            setQuickCreateLoading(false);
        }
    };

    return (
        <div className="mobile-shell shabnam-font viewport-height w-full flex flex-column surface-ground rtl overflow-hidden">
            <MobileHeader
                displayName={displayName}
                member={member}
                notificationCount={unreadCount}
                onNotificationsClick={() => navigate("/mobile/notifications")}
                onProfileClick={handleProfileClick}
                onWorkspaceClick={handleWorkspaceClick}
                translate={translate}
                workspaceName={workspaceName}
            />
            <OverlayPanel className="w-10rem rtl shabnam-font" ref={profileMenuRef} pt={{content: () => ({className: "p-0"})}}>
                <div className="flex flex-column gap-1">
                    <Button
                        type="button"
                        text
                        className="p-2 text-xs border-round-sm shadow-none justify-content-start"
                        label={translate("user-account")}
                        onClick={() => {
                            profileMenuRef.current?.hide();
                            navigate("/mobile/profile");
                        }}/>
                    <Button
                        type="button"
                        text
                        severity="danger"
                        className="p-2 text-xs border-round-sm shadow-none justify-content-start"
                        label={translate("sign-out")}
                        onClick={() => {
                            profileMenuRef.current?.hide();
                            dispatch(logout());
                        }}/>
                </div>
            </OverlayPanel>
            <SelectWorkspace ref={workspaceMenuRef} className="rtl w-20rem shabnam-font" createWorkspacePath="/mobile/workspace/create" onChange={() => navigate("/mobile")}/>
            {workspaceRestricted && <Message className="mx-3 mb-2 text-xs flex-shrink-0" severity="warn" text={translate(`workspace-status.${workspaceMembershipData?.workspace?.workspaceStatus}`)}/>} 
            <main className="mobile-shell-main flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 pt-2 pb-3">
                <MobileView
                    detailId={detailId}
                    navigate={navigate}
                    readOnly={readOnly}
                    section={effectiveSection}
                    translate={translate}
                    visibleMoreItems={visibleMoreItems}
                    onWorkspaceClick={handleWorkspaceClick}
                    notificationCount={unreadCount}
                />
            </main>
            <BottomNav activeSection={effectiveSection} navigate={navigate} onQuickCreate={openQuickCreate} translate={translate}/>
            <MobileQuickCreate
                visible={quickCreateVisible}
                onHide={() => setQuickCreateVisible(false)}
                types={quickCreateTypes}
                loading={quickCreateLoading}
                error={quickCreateError}
                onTypeChange={() => setQuickCreateError("")}
                onSubmit={createQuickItem}
                translate={translate}
            />
        </div>
    );
}

function MobileView({detailId, navigate, readOnly, section, translate, visibleMoreItems, onWorkspaceClick, notificationCount}) {
    const {workspaceRole} = useWorkspaceRole();
    if (section === "home") return <MobileHome navigate={navigate} readOnly={readOnly} translate={translate}/>;
    if (section === "tasks") return <MobileTasks detailId={detailId} navigate={navigate} readOnly={readOnly} translate={translate}/>;
    if (section === "messages") return <MobileMessages chatId={detailId} navigate={navigate} readOnly={readOnly} translate={translate}/>;
    if (section === "notifications") return <MobileMessages notificationMode navigate={navigate} translate={translate}/>;
    if (section === "notification-settings") return <NotificationSettings name="notification-settings"/>;
    if (section === "more") return <MobileMore items={visibleMoreItems} navigate={navigate} translate={translate}/>;
    if (section === "settings") return <MobileSettings canManageWorkspace={!readOnly && ["owner", "admin"].includes(workspaceRole?.role)} detailId={detailId} navigate={navigate} translate={translate} onWorkspaceClick={onWorkspaceClick} notificationCount={notificationCount}/>;
    if (section === "profile") return <MobileProfile navigate={navigate} translate={translate}/>;
    if (section === "workspace" && detailId === "create") return <CreateWorkspace closable onClose={() => navigate(-1)} onCreated={() => navigate("/mobile")}/>;
    if (["projects", "goals", "calendar", "teams", "help", "updates"].includes(section)) {
        return <MobileResource resource={section} detailId={detailId} navigate={navigate} translate={translate}/>;
    }
    return <MobileHome navigate={navigate} readOnly={readOnly} translate={translate}/>;
}

function MobileHeader({displayName, member, notificationCount, onNotificationsClick, onProfileClick, onWorkspaceClick, translate, workspaceName}) {
    return (
        <header className="mobile-shell-header flex-shrink-0 flex align-items-center justify-content-between gap-3 px-3 surface-ground">
            <div className="flex align-items-center gap-2 min-w-0">
                <span className="flex flex-column align-items-center flex-shrink-0">
                    <Button data-testid="mobile-profile-menu-trigger" type="button" text rounded className="surface-ground p-0 w-2rem h-2rem" onClick={onProfileClick} aria-label={displayName}><Avatar memberData={member} shape="circle" className="font-bold w-2rem h-2rem"/></Button>
                </span>
                <span className="flex flex-column min-w-0 gap-1">
                    <Button type="button" text className="surface-ground text-right p-0 justify-content-start" onClick={onProfileClick}><strong className="text-sm text-900 text-overflow-ellipsis overflow-hidden white-space-nowrap">{translate("mobile-shell.greeting").replace("{{name}}", displayName)}</strong></Button>
                    <button type="button" className="flex align-items-center gap-1 border-none surface-ground text-right p-0 cursor-pointer text-xs text-500" onClick={onWorkspaceClick}>{workspaceName}<i className="pi pi-angle-down text-2xs"/></button>
                </span>
            </div>
            <span className="relative flex-shrink-0">
                <Button type="button" rounded text className="w-2rem h-2rem surface-card mobile-brand-shadow" icon="pi pi-bell" aria-label={translate("mobile-shell.notifications")} onClick={onNotificationsClick}/>
                {notificationCount > 0 && <Badge value={notificationCount} severity="danger" className="absolute top-0 right-0 text-3xs min-w-1rem h-1rem"/>}
            </span>
        </header>
    );
}

function BottomNav({activeSection, navigate, onQuickCreate, translate}) {
    const parentSection = ["projects", "goals", "calendar", "teams", "workspace", "settings", "profile", "help", "updates", "notifications", "notification-settings"].includes(activeSection) ? "more" : activeSection;
    return (
        <footer className="mobile-shell-footer flex-shrink-0 w-full surface-ground">
        <nav data-testid="mobile-bottom-nav" className="mobile-bottom-nav flex align-items-center mx-2 px-2 py-1 surface-card border-1 border-100 mobile-brand-radius mobile-brand-shadow" aria-label={translate("mobile-shell.primary-navigation")}>
            {navigationItems.slice(0, 2).map((item) => <MobileNavItem key={item.key} item={item} active={parentSection === item.key} translate={translate} navigate={navigate}/>)}
            <div className="flex-1 min-h-4rem flex align-items-center justify-content-center p-0">
                <Button data-testid="mobile-quick-add" type="button" rounded className="w-3rem h-3rem -mt-1 p-0 mobile-brand-shadow " icon="pi pi-plus m-0 line-height-1" aria-label={translate("mobile-shell.quick-add")} onClick={onQuickCreate}/></div>
            {navigationItems.slice(2).map((item) => <MobileNavItem key={item.key} item={item} active={parentSection === item.key} translate={translate} navigate={navigate}/>)}
        </nav>
        </footer>
    );
}

function MobileNavItem({item, active, translate, navigate}) {
    return <button type="button" className={`flex-1 min-h-3rem flex flex-column align-items-center justify-content-center gap-1 border-none surface-card mobile-brand-radius py-2 px-1 cursor-pointer ${active ? "text-primary bg-primary-50" : "text-500"}`} aria-current={active ? "page" : undefined} onClick={() => navigate(item.path)}><i className={`${item.icon} text-xl`}/><span className="text-2xs white-space-nowrap">{translate(`mobile-shell.nav.${item.key}`)}</span></button>;
}

export default Mobile;
